import type { TitleBrief } from "@anisync/types";
import { StarIcon } from "lucide-react";
import { Link } from "react-router";
import { fadeIn, stagger } from "../lib/motion";
import { cn } from "../lib/utils";
import { Skeleton } from "./ui/skeleton";

const FORMAT_LABELS: Record<string, string> = {
  tv: "TV",
  tv_short: "TV krótkie",
  movie: "Film",
  special: "Special",
  ova: "OVA",
  ona: "ONA",
  music: "Muzyczne",
};

const STATUS_LABELS: Record<string, string> = {
  finished: "Zakończone",
  releasing: "Emitowane",
  not_yet_released: "Zapowiedziane",
  cancelled: "Anulowane",
  hiatus: "Wstrzymane",
};

/**
 * Kafel tytułu. Kolor przewodni z okładki (AniList podaje go w metadanych)
 * służy jako poświata na fokusie — dzięki temu wyróżnienie jest widoczne
 * z dystansu i różne dla każdego tytułu, bez ręcznego dobierania barw.
 *
 * Kafel ma własny pierścień (`focus-ring-own`), bo globalny `box-shadow` wokół
 * prostokąta okładki wygląda jak obwódka naklejona obok obrazu — tu wyróżnia
 * skala plus poświata w barwie tytułu.
 */
export function TitleCard({
  title,
  fluid = false,
  index,
}: {
  title: TitleBrief;
  fluid?: boolean;
  /** Pozycja w siatce — kafel wchodzi po kolei (`enter-rise`). Bez niej stoi od razu. */
  index?: number;
}) {
  const accent = title.colorHex ?? "var(--color-primary)";
  const label = title.titleEnglish ?? title.titleRomaji;
  const meta = [
    title.seasonYear !== null ? String(title.seasonYear) : null,
    title.format !== null ? (FORMAT_LABELS[title.format] ?? title.format) : null,
    title.episodesTotal !== null ? `${title.episodesTotal} odc.` : null,
  ].filter((x): x is string => x !== null);

  return (
    <Link
      to={`/tytul/${title.id}`}
      // W pasie kafel ma stałą szerokość, bo pas przewija się w poziomie; w siatce
      // (`fluid`) wypełnia kolumnę — inaczej stała szerokość wychodziła poza
      // kolumnę na telefonie albo zostawiała w niej pustkę na szerokim ekranie.
      className={cn(
        "focus-ring-own group block shrink-0 rounded-lg outline-none",
        fluid ? "w-full" : "w-[11rem]",
        index !== undefined && "enter-rise",
      )}
      {...(index !== undefined ? { style: stagger(index) } : {})}
      aria-label={label}
    >
      {/* Do czasu wczytania okładki stoi jej kolor przewodni, przygaszony —
          siatka kolorowych prostokątów zapowiada okładki lepiej niż szara. */}
      <div
        className="relative aspect-[2/3] overflow-hidden rounded-lg border border-border bg-card transition-transform duration-200 ease-tv group-hover:scale-[1.04] group-focus-visible:scale-[1.06]"
        {...(title.colorHex !== null
          ? {
              style: {
                backgroundColor: `color-mix(in oklab, ${title.colorHex} 22%, var(--color-card))`,
              },
            }
          : {})}
      >
        {title.coverUrl !== null ? (
          <img
            {...fadeIn}
            src={title.coverUrl}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center px-3 text-center text-sm text-muted-foreground">
            {title.titleRomaji}
          </div>
        )}

        {/* Wygaszenie od dołu trzyma plakietkę czytelną także na jasnej okładce. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent"
        />

        {/* Poświata w kolorze okładki — czyta się z dystansu lepiej niż obramowanie. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-lg opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
          style={{ boxShadow: `inset 0 0 0 2px ${accent}, 0 0 2.5rem -0.5rem ${accent}` }}
        />

        {title.averageScore !== null && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md bg-background/75 px-1.5 py-0.5 text-xs font-semibold backdrop-blur-sm">
            <StarIcon className="size-3 text-warning" />
            {title.averageScore}
          </span>
        )}

        {title.status === "releasing" && (
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-md bg-background/75 px-1.5 py-0.5 text-xs font-medium backdrop-blur-sm">
            <span className="size-1.5 rounded-full bg-success" />
            {STATUS_LABELS.releasing}
          </span>
        )}
      </div>

      <p className="mt-2 line-clamp-2 text-sm leading-snug font-medium">{label}</p>
      {meta.length > 0 && (
        <p className="mt-0.5 text-xs text-muted-foreground">{meta.join(" · ")}</p>
      )}
    </Link>
  );
}

/** Zastępczy kafel na czas wczytywania — trzyma układ, żeby siatka nie skakała. */
export function TitleCardSkeleton({ fluid = false }: { fluid?: boolean }) {
  return (
    <div className={cn("shrink-0", fluid ? "w-full" : "w-[11rem]")} aria-hidden="true">
      <Skeleton className="aspect-[2/3] rounded-lg" />
      <Skeleton className="mt-2 h-4 w-3/4" />
    </div>
  );
}
